import { z } from 'zod';
import { IdentityReadSchema } from '../integrations/integration.models.identity';
import { ResourceReadBaseSchema } from '../integrations/integration.models.resource';
import { ResourceSubscriptionBaseSchema } from '../integrations/integration.models.resource.subscriptions';
import { AccountIdSchema, AccountSchema } from '../models.accounts';
import { ConnectionReadSchema } from '../models.connections';
import {
  DateTimeTzSchema,
  GenericConnectionId,
  GenericIdentityUUID,
  GenericTimelyUserId,
  GenericUserID,
  GenericUUID,
} from '../models.generic';
import {
  TimelyResourceUserAssociationSchema,
  TimelySupportedResourceTypeEnumsSchema,
} from './models.timely.resources';

/**
 * Params/Input to query:
 */
export type rpc_QueryTimelyImportableDataParams = z.infer<
  typeof rpc_QueryTimelyImportableDataParamsSchema
>;
export const rpc_QueryTimelyImportableDataParamsSchema = z.object({
  allowed_types: z.array(TimelySupportedResourceTypeEnumsSchema),
  updated_after: DateTimeTzSchema,
  updated_before: DateTimeTzSchema,
  page: z.coerce.number().int().default(1).describe('Page number for pagination, starting at 1'),
  per_page: z.coerce
    .number()
    .int()
    .default(250)
    .describe('Number of records we query per page (250 default, down from 500)'),
  limit_to_account_id: AccountIdSchema.optional().describe(
    'If set, limits the diff to only resources belonging to this TIC account. Does NOT produce "trusted" diff'
  ),
  limit_to_connection_id: GenericConnectionId.optional().describe(
    'If set, limits the diff to only resources belonging to this TIC connection. Does NOT produce "trusted" diff'
  ),
  limit_to_identity_ids: z.array(GenericIdentityUUID).optional(),
  include_all_data: z
    .boolean()
    .optional()
    .default(false)
    .describe(
      'Whether to include all data or only incremental changes based on last_synced_at timestamps'
    ),
});

// Define the partial Resource model
export type ReturnedDiffResource = z.infer<typeof ReturnedDiffResourceSchema>;
const ReturnedDiffResourceSchema = ResourceReadBaseSchema.pick({
  id: true,
  uri: true,
  // type: true // we limit & coerce type to Timely compatible models, hence the extend below.
  title: true,
  tool_id: true,
  end_date: true,
  metadata: true,
  url_html: true,
  deleted_at: true,
  start_date: true,
  updated_at: true,
  archived_at: true,
  external_id: true,
  completed_at: true,
})
  .extend({ type: TimelySupportedResourceTypeEnumsSchema })
  .strict();

const ReturnedDiffRelatedMinimalResourceSchema = ReturnedDiffResourceSchema.pick({
  id: true,
  uri: true,
  type: true,
  title: true,
});

// Define the partial Connection model
const ReturnedDiffConnectionSchema = ConnectionReadSchema.pick({
  id: true,
  settings: true,
  updated_at: true,
  imported_data_access: true,
  managing_user_id: true,
})
  .extend({
    account_id: AccountIdSchema.optional(),
  })
  .strict();

// Define the partial Subscription model
const ReturnedDiffSubscriptionSchema = ResourceSubscriptionBaseSchema.pick({
  id: true,
  enabled: true,
  synced_at: true,
}).strict();

// Define the partial Account model
const ReturnedDiffAccountSchema = AccountSchema.pick({
  timely_account_id: true,
  synced_with_timely_at: true,
})
  .extend({ id: AccountIdSchema })
  .strict();

// Define the partial Identity model
export type ReturnedDiffIdentityRow = z.infer<typeof ReturnedDiffIdentityRowSchema>;
export const ReturnedDiffIdentityRowSchema = IdentityReadSchema.pick({
  id: true,
  verified: true,
  connection_id: true,
  external_user_id: true,
  external_username: true,
  external_email: true,
  created_at: true,
  updated_at: true,
  external_name: true,
  external_timezone: true,
  uri: true,
  tool_id: true,
  avatar_url: true,
  metadata: true, // due to strict, may want to omit instead if not needed
})
  .extend({
    // nullable since identities can be imported in batch/not my user verifying their identity,
    user_id: GenericUserID.nullable().openapi({
      description: 'The TIC `user.id` that has claimed this identity, if any.',
    }),
    user: z.object({
      // same as above, identities can now exist without having ties to Timely.
      timely_id: GenericTimelyUserId.nullable().openapi({
        description: 'The Timely id version of `user.id`, if known',
      }),
    }),
    connection: ReturnedDiffConnectionSchema.extend({
      account: ReturnedDiffAccountSchema,
    }),
  })
  .strict();

export type ReturnedDiffRow = z.infer<typeof ReturnedDiffRowSchema>;
export const ReturnedDiffRowSchema = z.object({
  resource: ReturnedDiffResourceSchema,
  /** Softer validation here, as older diffs won't have these values */
  resource_permissions: z
    .object({
      id: GenericUUID.nullable().openapi({
        description:
          'The TIC `resource_permissions.id` reference, to make updates to relationship easier',
      }),
      timely_id: z.number().int().min(1).nullable().openapi({
        description:
          'The Timely id version of `resource.id`, if resource has created a Timely data model',
        example: 2222,
      }),
    })
    .optional(),
  resource_root: ReturnedDiffRelatedMinimalResourceSchema.nullable(),
  resource_parent: ReturnedDiffRelatedMinimalResourceSchema.nullable(),
  resource_children: z.array(ReturnedDiffRelatedMinimalResourceSchema).nullable(),
  connection: ReturnedDiffConnectionSchema,
  // Nullable since TT-5630: tags/labels are exempt from the effective-subscription rule in the diff
  // query (they are non-subscribable, so requiring a subscribed ancestor made delivery depend on a
  // globally-shared parent pointer), and a row emitted under that exemption has no subscription.
  subscription: ReturnedDiffSubscriptionSchema.nullable(),
  account: ReturnedDiffAccountSchema,
  user_associations: z.array(TimelyResourceUserAssociationSchema).nullable(),
});

// Combine the models into the final schema
export type QueryReturnedDataDiff = z.infer<typeof QueryReturnedDataDiffSchema>;
export const QueryReturnedDataDiffSchema = z.object({
  identities: z.array(ReturnedDiffIdentityRowSchema),
  resources: z.array(ReturnedDiffRowSchema),
});

// Schema for ExecuteTimelyDataDiffResult meta response
export type ExecuteTimelyDataDiffMeta = z.infer<typeof ExecuteTimelyDataDiffMetaSchema>;
export const ExecuteTimelyDataDiffMetaSchema = z.object({
  requestId: z.string().describe('Unique identifier for the request'),
  duration: z.number().describe('Duration of the operation in milliseconds'),
  message: z.string().describe('Human-readable status message'),
  count: z.number().describe('Number of records processed'),
  foundDataToDiff: z.boolean().describe('Whether any data was found to diff'),
  query: rpc_QueryTimelyImportableDataParamsSchema.describe('The RPC query parameters used'),
});
